import SearchColumnKeyword from "@/views/list/components/SearchColumnKeyword.vue";

/**
 * 위험성평가(개선전 944 / 개선후 945) 작성 화면으로 이동
 * 연결된 글이 이미 있으면 그 글의 수정 화면으로, 없으면 823 글 ID 를 store 에 담아 신규 작성 화면으로 이동한다.
 * (823 Write.vue 의 movePage 와 동일한 동작)
 * @param vue : TableListPage 인스턴스 (prefixFunc 에서 전달)
 * @param boardId : 944 | 945
 * @param postId823 : 산업재해 발생현황 글 ID
 * @param connectPostId : 연결된 944/945 글 ID (없으면 null)
 */
// 위험성평가 뱃지 색상
const RISK_BADGE_COLOR = {
  완료: "#3C3C3C",
  미완료: "#FF9F43", // 내가 작성할 수 있는 글(작성자/시스템 관리자)
  미완료_타인: "#B9B9C3", // 다른 사람 글 — 클릭해도 이동하지 않음
};

const moveRiskAssessment = (vue, boardId, postId823, connectPostId) => {
  const params = {
    boardId: `${boardId}`,
    boardSlug: `board-${boardId}`,
    pageType: "write",
    proj_id: vue.projectId,
  };
  if (connectPostId) {
    vue.$router.push({ name: "write|update|tab", params: { ...params, postId: connectPostId } });
  } else {
    vue.$store.commit("board/SET_BOARD_CONNECT_823", postId823);
    vue.$router.push({ name: "write|tab", params });
  }
};

const list = {
  // =================================================================================================================
  components() {
    return [
      {
        component: "search-year",
        label: "대상년도",
        value: "year",
        query: { y: "year" },
        options: {
          default: new Date().getFullYear(),
          afterFix: "년",
        },
      },
      {
        component: "search-select-field",
        label: "사업장",
        value: "field",
        query: { f_id: "fieldId", f_name: "fieldName" },
        options: {
          default: { fieldId: "", fieldName: "" },
        },
      },
      {
        component: "search-column-keyword",
        label: "검색조건",
        value: "column",
        query: { ck_t: "head", ck_k: "keyword" },
        options: {
          hiddenList: ["사업장명", "대상년도", "재해일시", "근속년수", "해당직무근속년수", "복직일", "요양일수", "산재승인여부", "재해속보", "합동조사회의록", "재발방지대책", "개선결과", "위험성평가(개선전)", "위험성평가(개선후)"],
        },
      },
    ];
  },
  getConditions: (nav) => {
    const conditions = [];
    const search = nav.value;

    // 검색연도
    const year = search.year;
    if (year) {
      conditions.push({ key: "body.subInfo.재해일시", operator: ">=", value: `${year}-01-01` });
      conditions.push({ key: "body.subInfo.재해일시", operator: "<", value: `${year + 1}-01-01` });
    }

    // 사업장
    if (search.field) {
      conditions.push({ key: "body.stdInfo.field_id", operator: "=", value: search.field.fieldId });
    }

    //  검색조건
    const heads = list.getHead().map((e) => {
      return { [e.name]: e.key };
    });
    // 배열의 객체들을 하나의 객체로 합치기
    const types = Object.assign({}, ...heads);
    if (search.column && search.column.keyword) {
      conditions.push({ key: types[search.column.head], operator: "LIKE", value: `%${search.column.keyword}%` });
    }
    return conditions;
  },

  getDefaultSort: () => {
    return [{ target: "$body.subInfo.재해일시", direction: "desc" }];
  },

  /**
   * 목록을 불러온 이후 실제 데이터 값을 list 변수에 저장하기 전에 실행되는 함수 ( this.listAll = response.data 하기 전 )
   * 목록에 표기되는 데이터의 postId 를 배열로 만들어서 944, 945를 조회한다.
   * @param response : 목록데이터
   * @param options : 목록데이터를불러올때사용한옵션
   * @returns {Promise<*>}
   */
  prefixFunc: async (vue, response, options) => {
    const postIds = response.data.map((data) => data.id);
    const newResponse = { ...response };

    if (postIds && postIds.length > 0) {
      const conditions = [];
      conditions.push({ key: "body.connect823Id", operator: "IN", value: postIds });

      const apiOptions = {
        projectId: options.projectId,
        page: 1,
        perPage: options.perPage,
        orderTarget: "created_at",
        orderDirection: "desc",
        conditions,
      };

      // 944, 945 호출
      const [res944, res945] = await Promise.all([vue.$store.dispatch("approval/LOAD_POST_ALL", { ...apiOptions, boardSlug: `board-944` }), vue.$store.dispatch("approval/LOAD_POST_ALL", { ...apiOptions, boardSlug: `board-945` })]);

      const list944 = res944 && res944.data && res944.data.length > 0 ? res944.data : [];
      const list945 = res945 && res945.data && res945.data.length > 0 ? res945.data : [];

      // 823 글 ID 별로 연결된 944/945 글 ID 를 찾아 넣는다. (없으면 null)
      // ex) { [823글ID]: { 944: 944글ID, 945: null } }
      const jsonConnect = {};
      const setConnect = (boardId, post) => {
        const postId823 = JSON.parse(post.post_content).body.connect823Id;
        if (!postId823) return;
        if (!jsonConnect[postId823]) jsonConnect[postId823] = { 944: null, 945: null };
        // 최신순 정렬이므로 먼저 나온(최신) 글을 우선한다
        if (!jsonConnect[postId823][boardId]) jsonConnect[postId823][boardId] = post.post_id || post.id;
      };
      list944.forEach((post) => setConnect(944, post));
      list945.forEach((post) => setConnect(945, post));

      // 위험성평가 작성/수정 화면으로 이동할 수 있는 사람: 글 작성자 또는 시스템 관리자 (823 Write.vue 의 버튼 노출 조건과 동일)
      const myInfo = vue.$store.state.auth.userInfo || {};
      const myId = parseInt(myInfo.id);
      const isAdmin = parseInt(myInfo.is_superuser) > 0;

      // response.data 에 otherPosts(연결 글 ID), 이동 가능 여부, 위험성평가 바로가기 함수를 추가
      newResponse.data = newResponse.data.map((data) => {
        const otherPosts = jsonConnect[data.id] || { 944: null, 945: null };
        // 목록 응답은 작성자 관계(author)를 포함한다. 혹시 빠진 경우를 대비해 user_id 로도 확인한다.
        const authorId = parseInt(data.author && data.author.id ? data.author.id : data.user_id || data.author_id);
        const riskEditable = isAdmin || (myId > 0 && authorId === myId);
        return {
          ...data,
          otherPosts,
          riskEditable,
          /**
           * 뱃지 클릭 동작. 이동할 수 없는 경우(타인 글, 개선전 미작성)에는 상세로 가지 않고 알림만 띄운다.
           * @param boardId : 944 | 945
           */
          moveRiskAssessment: (boardId) => {
            if (!riskEditable) {
              vue.$vs.notify({ title: "알림", text: "작성자만 작성할 수 있습니다.", color: "warning" });
              return;
            }
            if (boardId === 945 && !otherPosts[944]) {
              vue.$vs.notify({ title: "알림", text: "위험성평가(개선전)를 먼저 작성해야 개선후를 작성할 수 있습니다.", color: "warning" });
              return;
            }
            moveRiskAssessment(vue, boardId, data.id, otherPosts[boardId]);
          },
        };
      });
    }

    return newResponse;
  },

  // =================================================================================================================
  getHead: () => {
    return [
      {
        name: "대상년도",
        key: "header.year",
      },
      {
        name: "사업장명",
        key: "body.stdInfo.field_name",
      },
      {
        name: "재해일시",
        key: "body.subInfo.재해일시",
      },
      {
        name: "재해장소",
        key: "body.subInfo.재해장소",
      },
      {
        name: "구분",
        key: "body.subInfo.구분",
      },
      {
        name: "팀명/업체명",
        key: "body.subInfo.팀명_업체명",
      },
      {
        name: "근속년수",
        key: "body.subInfo.근속년수",
      },
      {
        name: "해당직무근속년수",
        key: "body.subInfo.해당직무근속년수",
        style: { minWidth: "120px" },
      },
      {
        name: "재해유형",
        key: "body.subInfo.재해형태",
        style: { maxWidth: "100px" },
      },
      {
        name: "복직일",
        key: "body.subInfo.복직일",
      },
      {
        name: "요양일수",
        key: "body.subInfo.요양일수",
      },
      {
        name: "산재승인여부",
        key: "body.subInfo.산재승인여부",
      },
      {
        name: "재해속보",
        key: "body.subInfo.재해속보",
      },
      {
        name: "합동조사회의록",
        key: "body.subInfo.합동조사회의록",
        style: { minWidth: "120px" },
      },
      {
        name: "재발방지대책",
        key: "body.subInfo.재발방지대책",
      },
      {
        name: "개선결과",
        key: "body.subInfo.개선결과",
      },
      {
        name: "기타",
        key: "body.subInfo.기타",
      },
      {
        name: "위험성평가(개선전)",
        key: "body.connect944Id",
      },
      {
        name: "위험성평가(개선후)",
        key: "body.connect945Id",
      },
    ];
  },
  getCol: (index, value, func) => {
    try {
      const postContent = JSON.parse(value.post_content);
      switch (index) {
        case 0:
          return postContent.header.year; // 대상년도
        case 1:
          return func.getContent(value).stdInfo.field_name; // 사업장명
        case 2:
          return func.getContent(value).subInfo.재해일시; // 재해일시
        case 3:
          return func.getContent(value).subInfo.재해장소; // 재해장소
        case 4:
          return func.getContent(value).subInfo.구분; // 구분
        case 5:
          return func.getContent(value).subInfo.팀명_업체명; // 팀명/업체명
        case 6:
          return func.getContent(value).subInfo.근속년수; // 근속년수
        case 7:
          return func.getContent(value).subInfo.해당직무근속년수; // 해당직무근속년수
        case 8:
          return func.getContent(value).subInfo.재해형태; // 재해형태
        case 9:
          return func.getContent(value).subInfo.복직일; // 복직일
        case 10:
          return func.getContent(value).subInfo.요양일수; // 요양일수
        case 11: {
          // 산재승인여부
          const 산재 = func.getContent(value).subInfo.산재승인여부;
          const result = `<span class="text-${산재 === "승인" ? "success" : "danger"}">${산재}</span>`;
          return {
            type: "html",
            html: result,
          };
        }
        case 12: {
          // 재해속보
          const file = func.getContent(value).subInfo.재해속보;
          return {
            type: "docsIcon",
            file,
          };
        }
        case 13: {
          // 합동조사회의록
          const file = func.getContent(value).subInfo.합동조사회의록;
          return {
            type: "docsIcon",
            file,
          };
        }
        case 14: {
          // 재발방지대책
          const file = func.getContent(value).subInfo.재발방지대책;
          return {
            type: "docsIcon",
            file,
          };
        }
        case 15: {
          // 개선결과
          const file = func.getContent(value).subInfo.개선결과;
          return {
            type: "docsIcon",
            file,
          };
        }
        case 16:
          return func.getContent(value).subInfo.기타; // 기타
        case 17: // 개선전 — 클릭 시 944 작성(미완료) / 수정(완료) 화면으로 이동
          return list.getRiskAssessmentCol(value, 944);
        case 18: // 개선후 — 클릭 시 945 작성(미완료) / 수정(완료) 화면으로 이동
          return list.getRiskAssessmentCol(value, 945);
      }
    } catch (e) {
      /*ignore*/
    }
    return "";
  },
  /**
   * 위험성평가(개선전/개선후) 완료 여부 뱃지.
   * - 작성자/시스템 관리자(riskEditable): 클릭하면 상세 페이지가 아니라 위험성평가 작성(미완료)/수정(완료) 화면으로 바로 이동. 미완료는 주황색.
   * - 그 외 사용자: 미완료는 회색으로 구분하고, 클릭하면 상세로 가지 않고 "작성자/관리자만 작성 가능" 알림을 띄운다. 완료는 행 클릭(상세)만 동작.
   * - 개선후(945) 미완료는 개선전(944)이 없으면 이동 대신 "개선전 먼저 작성" 알림을 띄운다. (823 Write.vue 의 버튼 노출 조건과 동일)
   */
  getRiskAssessmentCol: (value, boardId) => {
    const otherPosts = value.otherPosts || {};
    const done = !!otherPosts[boardId];
    const editable = !!value.riskEditable;
    const hasHandler = typeof value.moveRiskAssessment === "function";
    const col = done ? { type: "colorBox", color: RISK_BADGE_COLOR.완료, text: "완료" } : { type: "colorBox", color: editable ? RISK_BADGE_COLOR.미완료 : RISK_BADGE_COLOR.미완료_타인, text: "미완료" };
    if (!hasHandler) return col;

    // 미완료: 누구나 클릭 가능 (이동 또는 알림). 완료: 작성자/관리자만 클릭 이동, 그 외는 행 클릭으로 상세 이동.
    if (!done || editable) {
      col.onClick = () => value.moveRiskAssessment(boardId);
      if (editable) col.title = done ? "위험성평가 보기/수정" : "위험성평가 작성";
    }
    return col;
  },
  getSearchKeyValue: (nav) => {},
  getSearchKeyword: (nav) => {},
};
export default list;
